import { useEffect, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import {
    HighlightCollection,
    highlightCollectionsActions,
    useDispatchHighlightCollections,
    useLazyHighlightCollections,
} from "contexts/highlightCollections";
import { useHighlighted } from "contexts/highlighted";
import { SelectionSet } from "utils/selectionSet";

import { ObjectVisibility } from "..";
import {
    selectDefaultVisibility,
    selectPlainIsolationActive,
    selectPlainIsolationSnapshotIds,
    selectPlainIsolationSnapshotVersion,
} from "../renderSlice";

/**
 * Registered in `ProcoreOnlyHooks`; standalone keeps the equivalent keep-set in the Shown
 * context instead. See {@link useUpdateShown}.
 */
export function useUpdateTransparentSelectionBase() {
    const visibility = useAppSelector(selectDefaultVisibility);
    const isTransparentMode = visibility === ObjectVisibility.Transparent;
    const plainIsolationActive = useAppSelector(selectPlainIsolationActive);
    const snapshotVersion = useAppSelector(selectPlainIsolationSnapshotVersion);
    const requestedSnapshotIds = useAppSelector(selectPlainIsolationSnapshotIds);
    const dispatchHighlightCollection = useDispatchHighlightCollections();
    const highlightedCollections = useLazyHighlightCollections();
    const highlightedIdArr = useHighlighted().idArr;
    const appliedSnapshotVersion = useRef(0);

    useEffect(() => {
        const transparentSelectionBase = highlightedCollections.current[HighlightCollection.TransparentSelectionBase];
        if (isTransparentMode) {
            // Plain Isolate owns a fixed keep-set captured by isolateObjects /
            // unisolate undo. Later selections only highlight; they do not change it.
            if (plainIsolationActive) {
                if (appliedSnapshotVersion.current !== snapshotVersion) {
                    appliedSnapshotVersion.current = snapshotVersion;
                    dispatchHighlightCollection(
                        highlightCollectionsActions.setIds(
                            HighlightCollection.TransparentSelectionBase,
                            SelectionSet.fromIds(requestedSnapshotIds ?? []),
                        ),
                    );
                }
                return;
            }
            // If more objects selected - add them to the base, so they behave the same way
            const moreIds = highlightedIdArr.filter((id) => !transparentSelectionBase.ids.has(id));
            if (moreIds.length === 0) {
                return;
            }

            dispatchHighlightCollection(
                highlightCollectionsActions.add(HighlightCollection.TransparentSelectionBase, moreIds),
            );
        } else if (transparentSelectionBase.idArr.length > 0) {
            dispatchHighlightCollection(
                highlightCollectionsActions.setIds(HighlightCollection.TransparentSelectionBase, []),
            );
        }
    }, [
        dispatchHighlightCollection,
        highlightedIdArr,
        highlightedCollections,
        isTransparentMode,
        plainIsolationActive,
        requestedSnapshotIds,
        snapshotVersion,
    ]);
}
